import React, { useState, useRef, createElement } from 'react';
import { DownloadIcon, CopyIcon, RefreshCwIcon } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { toPng } from 'html-to-image';
import DashboardLayout from '../../components/layout/DashboardLayout';
// Default merchant (AQS Store)
const defaultMerchant = {
  id: 1,
  name: 'AQS Store',
  mid: '050102100023650',
  tid: '2365010001',
  bankBic: 'HABBPKKA',
  iban: 'PK73FAYS3555391000000000',
  mcc: '5204',
  currency: '586',
  city: 'Karachi',
  country: 'PK'
};
// Helper function to generate a unique transaction ID
const generateTransactionId = () => {
  return 'TX' + Math.random().toString(36).substring(2, 10).toUpperCase();
};
// Helper function to calculate CRC16-CCITT
const calculateCRC = (data: string): string => {
  let crc = 0xffff;
  for (let i = 0; i < data.length; i++) {
    crc ^= data.charCodeAt(i) << 8;
    for (let j = 0; j < 8; j++) {
      if (crc & 0x8000) {
        crc = crc << 1 ^ 0x1021;
      } else {
        crc = crc << 1;
      }
    }
  }
  return (crc & 0xffff).toString(16).toUpperCase().padStart(4, '0');
};
// Helper function to format EMV tag-length-value
const formatTLV = (tag: string, value: string): string => {
  const length = value.length.toString().padStart(2, '0');
  return `${tag}${length}${value}`;
};
// Create EMV Co. compliant QR code string
const createEMVQRString = data => {
  const {
    merchant,
    amount,
    includeAmount,
    tag62Fields
  } = data;
  let qrString = '';
  // Tag 00: Payload Format Indicator
  qrString += formatTLV('00', '01');
  // Tag 01: Point of Initiation Method (11 = Dynamic)
  qrString += formatTLV('01', '11');
  // Tag 26-51: Merchant Account Information
  let merchantAccountInfo = formatTLV('00', merchant.country);
  merchantAccountInfo += formatTLV('01', merchant.mid);
  merchantAccountInfo += formatTLV('02', merchant.tid);
  merchantAccountInfo += formatTLV('03', merchant.iban);
  qrString += formatTLV('26', merchantAccountInfo);
  // Tag 52: Merchant Category Code
  qrString += formatTLV('52', merchant.mcc);
  // Tag 53: Transaction Currency
  qrString += formatTLV('53', merchant.currency);
  // Tag 54: Transaction Amount (if included)
  if (includeAmount && amount) {
    qrString += formatTLV('54', amount);
  }
  // Tag 58: Country Code
  qrString += formatTLV('58', merchant.country);
  // Tag 59: Merchant Name
  qrString += formatTLV('59', merchant.name);
  // Tag 60: Merchant City
  qrString += formatTLV('60', merchant.city);
  // Tag 62: Additional Data Field Template
  let additionalData = '';
  // Add transaction ID
  const txnId = generateTransactionId();
  additionalData += formatTLV('05', txnId);
  // Add enabled Tag 62 fields
  if (tag62Fields.billNumber.enabled && tag62Fields.billNumber.value) {
    additionalData += formatTLV('01', tag62Fields.billNumber.value);
  }
  if (tag62Fields.mobileNumber.enabled && tag62Fields.mobileNumber.value) {
    additionalData += formatTLV('02', tag62Fields.mobileNumber.value);
  }
  if (tag62Fields.referenceLabel.enabled && tag62Fields.referenceLabel.value) {
    additionalData += formatTLV('07', tag62Fields.referenceLabel.value);
  }
  if (tag62Fields.purposeOfTransaction.enabled && tag62Fields.purposeOfTransaction.value) {
    additionalData += formatTLV('08', tag62Fields.purposeOfTransaction.value);
  }
  if (additionalData) {
    qrString += formatTLV('62', additionalData);
  }
  // Tag 63: CRC (placeholder)
  qrString += '6304';
  // Calculate and append CRC
  const crc = calculateCRC(qrString);
  qrString += crc;
  return qrString;
};
const DynamicPaymentQRCPage = () => {
  const qrCodeRef = useRef<HTMLDivElement>(null);
  const [selectedMerchant] = useState(defaultMerchant);
  const [includeAmount, setIncludeAmount] = useState(false);
  const [amount, setAmount] = useState('');
  const [qrCodeGenerated, setQrCodeGenerated] = useState(false);
  const [qrCodeString, setQrCodeString] = useState('');
  const [formErrors, setFormErrors] = useState({});
  // State for optional Tag 62 fields
  const [tag62Fields, setTag62Fields] = useState({
    billNumber: {
      value: '',
      enabled: false
    },
    mobileNumber: {
      value: '',
      enabled: false
    },
    storeLabel: {
      value: '',
      enabled: false
    },
    loyaltyNumber: {
      value: '',
      enabled: false
    },
    referenceLabel: {
      value: '',
      enabled: false
    },
    customerLabel: {
      value: '',
      enabled: false
    },
    terminalLabel: {
      value: '',
      enabled: false
    },
    purposeOfTransaction: {
      value: '',
      enabled: false
    },
    additionalConsumerData: {
      value: '',
      enabled: false
    },
    merchantTaxId: {
      value: '',
      enabled: false
    },
    merchantChannel: {
      value: '',
      enabled: false
    },
    dueDate: {
      value: '',
      enabled: false
    },
    amountAfterDueDate: {
      value: '',
      enabled: false
    }
  });
  // Handle tag 62 field toggle
  const handleTag62Toggle = field => {
    setTag62Fields({
      ...tag62Fields,
      [field]: {
        ...tag62Fields[field],
        enabled: !tag62Fields[field].enabled
      }
    });
  };
  // Handle tag 62 field value change
  const handleTag62Change = (field, value) => {
    setTag62Fields({
      ...tag62Fields,
      [field]: {
        ...tag62Fields[field],
        value
      }
    });
  };
  // Validate form before generating QR code
  const validateForm = () => {
    const errors = {};
    if (includeAmount) {
      if (!amount) {
        errors.amount = 'Amount is required';
      } else if (!/^\d+(\.\d{1,2})?$/.test(amount)) {
        errors.amount = 'Amount must be a valid number';
      } else if (amount.replace('.', '').length > 13) {
        errors.amount = 'Amount cannot exceed 13 digits';
      }
    }
    // Validate enabled Tag 62 fields
    Object.entries(tag62Fields).forEach(([key, field]) => {
      if (field.enabled && !field.value) {
        errors[key] = 'This field is required';
      }
      // Specific validations
      if (field.enabled && field.value) {
        if (key === 'mobileNumber' && !/^\d{1,15}$/.test(field.value)) {
          errors[key] = 'Mobile number must contain only digits (max 15)';
        } else if (key === 'dueDate' && !/^\d{8}$/.test(field.value)) {
          errors[key] = 'Due date must be in DDMMYYYY format';
        } else if (key === 'merchantChannel' && field.value.length !== 3) {
          errors[key] = 'Merchant channel must be exactly 3 characters';
        } else if (key === 'amountAfterDueDate' && !/^\d+(\.\d{1,2})?$/.test(field.value)) {
          errors[key] = 'Amount must be a valid number';
        }
        // Check length constraints for all fields
        const maxLengths = {
          billNumber: 25,
          mobileNumber: 15,
          storeLabel: 25,
          loyaltyNumber: 25,
          referenceLabel: 25,
          customerLabel: 25,
          terminalLabel: 25,
          purposeOfTransaction: 25,
          additionalConsumerData: 25,
          merchantTaxId: 25,
          merchantChannel: 3,
          dueDate: 8,
          amountAfterDueDate: 13
        };
        if (field.value.length > maxLengths[key]) {
          errors[key] = `Maximum length is ${maxLengths[key]} characters`;
        }
      }
    });
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  // Generate EMV QR code
  const generateQRCode = () => {
    if (!validateForm()) {
      return;
    }
    // Create the QR code string
    const qrString = createEMVQRString({
      merchant: selectedMerchant,
      amount,
      includeAmount,
      tag62Fields
    });
    setQrCodeString(qrString);
    setQrCodeGenerated(true);
  };
  // Handle copy QR code
  const handleCopyQRCode = () => {
    navigator.clipboard.writeText(qrCodeString).then(() => {
      alert('QR code string copied to clipboard!');
    }).catch(err => {
      console.error('Could not copy text: ', err);
    });
  };
  // Handle download QR code
  const handleDownloadQRCode = () => {
    if (qrCodeRef.current) {
      toPng(qrCodeRef.current, {
        quality: 1,
        pixelRatio: 2,
        backgroundColor: '#ffffff'
      }).then(dataUrl => {
        const link = document.createElement('a');
        link.download = `QR-Dynamic-${selectedMerchant.mid}.png`;
        link.href = dataUrl;
        link.click();
      }).catch(err => {
        console.error('Error downloading QR code:', err);
      });
    }
  };
  return <DashboardLayout data-id="element-1557">
      <div className="mb-6" data-id="element-1558">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1559">
          Dynamic Payment QR Code
        </h1>
        <p className="text-gray-600" data-id="element-1560">
          Generate an EMV Co. compliant dynamic payment QR code
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1561">
        {/* Left Column - Form */}
        <div className="space-y-6" data-id="element-1562">
          {/* Merchant Details */}
          <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-1563">
            <h2 className="text-lg font-semibold mb-4" data-id="element-1564">Merchant Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1565">
              <div data-id="element-1566">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1567">
                  Merchant Name
                </label>
                <div className="p-2 bg-gray-50 rounded border border-gray-200" data-id="element-1568">
                  {selectedMerchant.name}
                </div>
              </div>
              <div data-id="element-1569">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1570">
                  Merchant ID (MID)
                </label>
                <div className="p-2 bg-gray-50 rounded border border-gray-200" data-id="element-1571">
                  {selectedMerchant.mid}
                </div>
              </div>
              <div data-id="element-1572">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1573">
                  Till ID (TID)
                </label>
                <div className="p-2 bg-gray-50 rounded border border-gray-200" data-id="element-1574">
                  {selectedMerchant.tid}
                </div>
              </div>
              <div data-id="element-1575">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1576">
                  Bank BIC
                </label>
                <div className="p-2 bg-gray-50 rounded border border-gray-200" data-id="element-1577">
                  {selectedMerchant.bankBic}
                </div>
              </div>
              <div data-id="element-1578">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1579">
                  IBAN
                </label>
                <div className="p-2 bg-gray-50 rounded border border-gray-200" data-id="element-1580">
                  {selectedMerchant.iban}
                </div>
              </div>
              <div data-id="element-1581">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1582">
                  Category Code (MCC)
                </label>
                <div className="p-2 bg-gray-50 rounded border border-gray-200" data-id="element-1583">
                  {selectedMerchant.mcc}
                </div>
              </div>
              <div data-id="element-1584">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1585">
                  Currency
                </label>
                <div className="p-2 bg-gray-50 rounded border border-gray-200" data-id="element-1586">
                  {selectedMerchant.currency} (PKR)
                </div>
              </div>
              <div data-id="element-1587">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1588">
                  City
                </label>
                <div className="p-2 bg-gray-50 rounded border border-gray-200" data-id="element-1589">
                  {selectedMerchant.city}
                </div>
              </div>
            </div>
          </div>
          {/* Amount Section */}
          <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-1590">
            <div className="flex items-center justify-between mb-4" data-id="element-1591">
              <h2 className="text-lg font-semibold" data-id="element-1592">Amount</h2>
              <label className="flex items-center" data-id="element-1593">
                <input type="checkbox" checked={includeAmount} onChange={() => setIncludeAmount(!includeAmount)} className="h-4 w-4 text-blue-600 focus:ring-blue-500" data-id="element-1594" />
                <span className="ml-2 text-sm text-gray-600" data-id="element-1595">
                  Include Amount
                </span>
              </label>
            </div>
            {includeAmount && <div data-id="element-1596">
                <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1597">
                  Amount (PKR)
                </label>
                <input type="text" id="amount" value={amount} onChange={e => setAmount(e.target.value)} className={`w-full p-2 border ${formErrors.amount ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`} placeholder="Enter amount" data-id="element-1598" />
                {formErrors.amount && <p className="mt-1 text-sm text-red-600" data-id="element-1599">
                    {formErrors.amount}
                  </p>}
              </div>}
          </div>
          {/* Optional Tag 62 Fields */}
          <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-1600">
            <h2 className="text-lg font-semibold mb-4" data-id="element-1601">Optional Fields</h2>
            <div className="space-y-4" data-id="element-1602">
              {/* Bill Number */}
              <div className="flex flex-col" data-id="element-1603">
                <div className="flex items-center justify-between mb-1" data-id="element-1604">
                  <label htmlFor="billNumber" className="text-sm font-medium text-gray-700" data-id="element-1605">
                    Bill Number
                  </label>
                  <label className="flex items-center" data-id="element-1606">
                    <input type="checkbox" checked={tag62Fields.billNumber.enabled} onChange={() => handleTag62Toggle('billNumber')} className="h-4 w-4 text-blue-600 focus:ring-blue-500" data-id="element-1607" />
                    <span className="ml-2 text-xs text-gray-500" data-id="element-1608">Enable</span>
                  </label>
                </div>
                {tag62Fields.billNumber.enabled && <>
                    <input type="text" id="billNumber" value={tag62Fields.billNumber.value} onChange={e => handleTag62Change('billNumber', e.target.value)} className={`w-full p-2 border ${formErrors.billNumber ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`} placeholder="Enter bill number (max 25 chars)" maxLength={25} data-id="element-1609" />
                    {formErrors.billNumber && <p className="mt-1 text-sm text-red-600" data-id="element-1610">
                        {formErrors.billNumber}
                      </p>}
                  </>}
              </div>
              {/* Mobile Number */}
              <div className="flex flex-col" data-id="element-1611">
                <div className="flex items-center justify-between mb-1" data-id="element-1612">
                  <label htmlFor="mobileNumber" className="text-sm font-medium text-gray-700" data-id="element-1613">
                    Mobile Number
                  </label>
                  <label className="flex items-center" data-id="element-1614">
                    <input type="checkbox" checked={tag62Fields.mobileNumber.enabled} onChange={() => handleTag62Toggle('mobileNumber')} className="h-4 w-4 text-blue-600 focus:ring-blue-500" data-id="element-1615" />
                    <span className="ml-2 text-xs text-gray-500" data-id="element-1616">Enable</span>
                  </label>
                </div>
                {tag62Fields.mobileNumber.enabled && <>
                    <input type="text" id="mobileNumber" value={tag62Fields.mobileNumber.value} onChange={e => handleTag62Change('mobileNumber', e.target.value)} className={`w-full p-2 border ${formErrors.mobileNumber ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`} placeholder="Enter mobile number (max 15 digits)" maxLength={15} data-id="element-1617" />
                    {formErrors.mobileNumber && <p className="mt-1 text-sm text-red-600" data-id="element-1618">
                        {formErrors.mobileNumber}
                      </p>}
                  </>}
              </div>
              {/* Purpose of Transaction */}
              <div className="flex flex-col" data-id="element-1619">
                <div className="flex items-center justify-between mb-1" data-id="element-1620">
                  <label htmlFor="purposeOfTransaction" className="text-sm font-medium text-gray-700" data-id="element-1621">
                    Purpose of Transaction
                  </label>
                  <label className="flex items-center" data-id="element-1622">
                    <input type="checkbox" checked={tag62Fields.purposeOfTransaction.enabled} onChange={() => handleTag62Toggle('purposeOfTransaction')} className="h-4 w-4 text-blue-600 focus:ring-blue-500" data-id="element-1623" />
                    <span className="ml-2 text-xs text-gray-500" data-id="element-1624">Enable</span>
                  </label>
                </div>
                {tag62Fields.purposeOfTransaction.enabled && <>
                    <input type="text" id="purposeOfTransaction" value={tag62Fields.purposeOfTransaction.value} onChange={e => handleTag62Change('purposeOfTransaction', e.target.value)} className={`w-full p-2 border ${formErrors.purposeOfTransaction ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`} placeholder="Enter purpose (max 25 chars)" maxLength={25} data-id="element-1625" />
                    {formErrors.purposeOfTransaction && <p className="mt-1 text-sm text-red-600" data-id="element-1626">
                        {formErrors.purposeOfTransaction}
                      </p>}
                  </>}
              </div>
              {/* Reference Label */}
              <div className="flex flex-col" data-id="element-1627">
                <div className="flex items-center justify-between mb-1" data-id="element-1628">
                  <label htmlFor="referenceLabel" className="text-sm font-medium text-gray-700" data-id="element-1629">
                    Reference Label
                  </label>
                  <label className="flex items-center" data-id="element-1630">
                    <input type="checkbox" checked={tag62Fields.referenceLabel.enabled} onChange={() => handleTag62Toggle('referenceLabel')} className="h-4 w-4 text-blue-600 focus:ring-blue-500" data-id="element-1631" />
                    <span className="ml-2 text-xs text-gray-500" data-id="element-1632">Enable</span>
                  </label>
                </div>
                {tag62Fields.referenceLabel.enabled && <>
                    <input type="text" id="referenceLabel" value={tag62Fields.referenceLabel.value} onChange={e => handleTag62Change('referenceLabel', e.target.value)} className={`w-full p-2 border ${formErrors.referenceLabel ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`} placeholder="Enter reference label (max 25 chars)" maxLength={25} data-id="element-1633" />
                    {formErrors.referenceLabel && <p className="mt-1 text-sm text-red-600" data-id="element-1634">
                        {formErrors.referenceLabel}
                      </p>}
                  </>}
              </div>
            </div>
          </div>
          {/* Action Button */}
          <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-1635">
            <button onClick={generateQRCode} className="w-full px-4 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center" data-id="element-1636">
              <RefreshCwIcon className="h-5 w-5 mr-2" data-id="element-1637" />
              Generate EMV QR Code
            </button>
          </div>
        </div>
        {/* Right Column - QR Code Display */}
        <div className="bg-white rounded-lg shadow-sm p-6 flex flex-col" data-id="element-1638">
          <h2 className="text-lg font-semibold mb-4" data-id="element-1639">QR Code Preview</h2>
          {qrCodeGenerated ? <div className="flex flex-col items-center justify-center flex-grow" data-id="element-1640">
              <div ref={qrCodeRef} className="bg-white p-6 flex flex-col items-center" data-id="element-1641">
                <div className="mb-6 p-4 bg-white border-2 border-gray-300 rounded-lg inline-block" data-id="element-1642">
                  <QRCodeSVG id="qr-code-canvas" value={qrCodeString} size={256} level="M" includeMargin={true} fgColor="#000000" bgColor="#FFFFFF" data-id="element-1643" />
                </div>
                {includeAmount && amount && <div className="text-center mb-4" data-id="element-1644">
                    <p className="text-lg font-semibold text-blue-600" data-id="element-1645">
                      ₨{amount}
                    </p>
                  </div>}
                <div className="flex justify-center space-x-4 items-center" data-id="element-1646">
                  <img src="https://cdn.magicpatterns.com/uploads/uyg5nASZDGjrCe2rJaNmzm/Visa_Logo.png" alt="Visa" className="h-8 object-contain" data-id="element-1647" />
                  <img src="https://cdn.magicpatterns.com/uploads/wTaFUzAaMXmqM9o6YPo8t5/MasterCard_Logo.png" alt="MasterCard" className="h-8 object-contain" data-id="element-1648" />
                  <img src="https://cdn.magicpatterns.com/uploads/8qxbwmnN4pa5FLphCCtDLR/UnionPay_Logo.png" alt="UnionPay" className="h-8 object-contain" data-id="element-1649" />
                  <img src="https://cdn.magicpatterns.com/uploads/vGk29PVKWf7oAsHTkLwRwU/PayPak_Logo.png" alt="PayPak" className="h-8 object-contain" data-id="element-1650" />
                  <img src="https://cdn.magicpatterns.com/uploads/dW1dUF5BFKkWuGeGttfoz6/RAAST_Logo.png" alt="RAAST" className="h-8 object-contain" data-id="element-1651" />
                </div>
              </div>
              <div className="mb-6 w-full" data-id="element-1652">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1653">
                  QR Code String (EMV Co. Format)
                </label>
                <div className="p-2 bg-gray-50 rounded border border-gray-200 text-xs overflow-auto max-h-24" data-id="element-1654">
                  {qrCodeString}
                </div>
              </div>
              <div className="w-full grid grid-cols-2 gap-4" data-id="element-1655">
                <button onClick={handleCopyQRCode} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors flex items-center justify-center" data-id="element-1656">
                  <CopyIcon className="h-4 w-4 mr-2" data-id="element-1657" />
                  Copy QR String
                </button>
                <button onClick={handleDownloadQRCode} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center" data-id="element-1658">
                  <DownloadIcon className="h-4 w-4 mr-2" data-id="element-1659" />
                  Download QR
                </button>
              </div>
            </div> : <div className="flex flex-col items-center justify-center flex-grow" data-id="element-1660">
              <div className="h-64 w-64 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center mb-4" data-id="element-1661">
                <p className="text-gray-500" data-id="element-1662">QR Code will appear here</p>
              </div>
              <p className="text-gray-500 text-center" data-id="element-1663">
                Fill in the required fields and click 'Generate QR Code'
              </p>
            </div>}
        </div>
      </div>
    </DashboardLayout>;
};
export default DynamicPaymentQRCPage;